<template>
  <div
    ref="scrollerElement"
    class="glide"
    @click.capture="onClick"
    @mousedown="onMousedown"
  >
    <!-- ACHTUNG! Die Klasse .scroller-skiplink wird in dpc-scroller-item.vue referenziert -->
    <!-- v-if="isEnabled && !config.disableSkipLink" -->
    <slot name="before-track" />

    <a
      :id="`${id}-skiplink`"
      :href="`#skip-slider-${id}`"
      class="glide-skiplink"
    >
      <a-label path="skipScroller" :is-visible="true"></a-label>
    </a>

    <div :id="`${id}-track`" class="glide__track" data-glide-el="track">
      <ul class="glide__slides">
        <slot />
      </ul>
    </div>

    <div
      class="glide__controls"
      :class="{ 'glide__controls--has-dots': hasDots }"
    >
      <!-- TODO: use correct values for length -->
      <dpc-scroller-controls
        :has-button-next="hasButtonNext"
        :has-button-previous="hasButtonPrevious"
        :has-dots="hasDots"
        :current-index="currentIndex"
        :length="3"
        :aria-controls="`${id}-track`"
        @previous="go('<1')"
        @next="go('>1')"
        @select="index => go(`=${index}`)"
      />

      <slot name="controls" />
    </div>

    <slot name="after-track" />

    <div
      :id="`skip-slider-${id}`"
      class="glide-skiptarget"
      tabindex="-1"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
import {
  onMounted,
  ref,
  nextTick,
  provide,
  onBeforeUpdate,
  readonly,
  toRefs,
  watch,
  computed,
  watchEffect,
  markRaw,
  onBeforeUnmount,
} from 'vue';
import Glide, {
  Controls,
  Breakpoints,
  Swipe,
} from '@glidejs/glide/dist/glide.modular.esm';
import { Accessible } from '@js/glide-extensions/accessible';
import { VisibleClass } from '@js/glide-extensions/visible-class';
import { uuid } from 'vue-uuid';
import { useStore } from 'vuex';
import { useWindowSize, watchDebounced, watchThrottled } from '@vueuse/core';

let oneForth = window.innerWidth / 4;
let oneSixth = window.innerWidth / 6;
let oneEighth = window.innerWidth / 8;

const CONFIG_PRESETS = markRaw({
  default: {
    type: 'carousel',
    startAt: 0,
    perView: 1,
  },
  'ticket-data': {
    type: 'carousel',
    startAt: 0,
    perView: 1,
  },
  'socialmediateaser-default': {
    perView: 1,
    // rewind: false,
    // type: 'carousel',
    gap: 20,
    breakpoints: {
      desktop: {
        gap: 50,
        perView: 2,
      },
      tablet: {
        gap: 30,
        perView: 2,
      },
    },
  },
  'teaser-default': {
    perView: 1,
    // rewind: false,
    gap: 20,
    breakpoints: {
      desktop: {
        gap: 50,
        perView: 3,
      },
      tablet: {
        gap: 30,
        perView: 2,
      },
    },
  },
  'teaser-scroller': {
    type: 'slider',
    // type: 'carousel',
    startAt: 1,
    perView: 1,
    gap: 20,
    focusAt: 'center',
    peek: {
      before: 0,
      after: 0,
    },
    breakpoints: {
      'teaser-scroller': {
        gap: 50,
        peek: {
          get before() {
            return oneForth;
          },
          set before(value) {},
          get after() {
            return oneForth;
          },
          set after(value) {},
        },
      },
      desktop: {
        gap: 50,
        peek: {
          get before() {
            return oneSixth;
          },
          set before(value) {},
          get after() {
            return oneSixth;
          },
          set after(value) {},
        },
      },
      tablet: {
        gap: 30,
        peek: {
          get before() {
            return oneEighth;
          },
          set before(value) {},
          get after() {
            return oneEighth;
          },
          set after(value) {},
        },
      },
    },
  },
  calendar: {
    perView: 9,
    breakpoints: {
      desktop: {
        perView: 7,
      },
      tablet: {
        perView: 3,
      },
    },
  },
});

// TODO: check breakpoints

export default {
  props: {
    hasButtonNext: {
      type: Boolean,
      default: true,
    },
    hasButtonPrevious: {
      type: Boolean,
      default: true,
    },
    hasDots: {
      type: Boolean,
      default: false,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    config: {
      type: Object,
      default() {
        return {};
      },
    },

    configPreset: {
      type: String,
      validator(value) {
        const keys = Object.keys(CONFIG_PRESETS);
        const valid = keys.includes(value);

        if (!valid) {
          const keysAsString = keys.join('", "');
          throw new Error(
            `'${value}' is not a valid preset! Valid presets: '${keysAsString}'`
          );
        }

        return valid;
      },
      default: 'default',
    },
  },
  emits: ['run', 'update:currentIndex'],
  setup(props, { slots, emit }) {
    const store = useStore();
    const id = readonly(uuid.v1());
    const scrollerElement = ref(undefined);
    const glide = ref(undefined);
    const { currentIndex, configPreset } = toRefs(props);

    onMounted(() => {
      const configObject = computed(() => {
        const config = {
          ...CONFIG_PRESETS[props.configPreset || 'default'],
          ...props.config,
        };

        return store.getters['window/mergeConfigByBreakpoint'](config);
      });

      nextTick(() => {
        glide.value = new Glide(scrollerElement.value, {
          ...configObject.value.config,
        });
        glide.value.on('run', () => {
          emit('update:currentIndex', glide.value.index);
          emit('run', glide.value.index);
        });
        glide.value.mount({
          Controls,
          Swipe,
          VisibleClass,
          Accessible,
        });

        watch(
          () => configObject.value.lastValidBreakpoint,
          () => {
            glide.value.update(configObject.value.config);
          }
        );

        if (configPreset.value === 'teaser-scroller') {
          const { width: windowWidth } = useWindowSize();

          watchThrottled(
            windowWidth,
            windowWidth => {
              oneForth = windowWidth / 4;
              oneSixth = windowWidth / 6;
              oneEighth = windowWidth / 8;
              glide.value.update();
              console.log('update', { windowWidth });
            },
            { throttle: 200 }
          );
        }
      });
    });

    watch(currentIndex, currentIndex => {
      glide.value.go(`=${currentIndex}`);
    });

    const go = pattern => {
      glide.value.go(pattern);
    };

    const registerItem = item => {
      if (!glide.value) return;
      glide.value.update();
    };

    provide('scroller/registerItem', registerItem);

    /**
     * Prevent click event if the user is dragging
     */
    const CLICK_THRESHOLD = 15;
    const preventClick = ref(false);
    const xStart = ref(0);
    const xEnd = ref(0);
    const onMousedown = event => {
      xStart.value = event.clientX;
      console.log('start', xStart.value);
    };
    const onWindowMouseup = event => {
      xEnd.value = event.clientX;
      const delta = Math.abs(xStart.value - xEnd.value);

      if (delta > CLICK_THRESHOLD) {
        preventClick.value = true;
        setTimeout(() => {
          preventClick.value = false;
        }, 100);
      }
      return false;
    };
    onMounted(() => {
      window.addEventListener('mouseup', onWindowMouseup);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('mouseup', onWindowMouseup);
    });

    const onClick = event => {
      if (!preventClick.value) return;
      event.preventDefault();
      event.stopPropagation();
    };

    return { scrollerElement, glide, id, go, onClick, onMousedown };
  },
};
</script>
